import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import type {
  BookShelfConfig,
  BookShelfType,
} from "../../../../types/floorplannerTypes";
import {
  BOOK_SHELF_MAX_DEPTH_M,
  BOOK_SHELF_MAX_HEIGHT_M,
  BOOK_SHELF_MAX_SHELF_COUNT,
  BOOK_SHELF_MAX_SHELF_SPACING_M,
  BOOK_SHELF_MAX_WIDTH_M,
  BOOK_SHELF_MIN_DEPTH_M,
  BOOK_SHELF_MIN_HEIGHT_M,
  BOOK_SHELF_MIN_SHELF_COUNT,
  BOOK_SHELF_MIN_SHELF_SPACING_M,
  BOOK_SHELF_MIN_WIDTH_M,
  deriveBookShelfSpacingFromHeight,
  normalizeBookShelfConfig,
} from "../../../../features/floorplan3d/symbols/livingRoom/bookShelfConfig";
import DimensionField from "../components/DimensionField";
import NumberField from "../components/NumberField";
import RotationField from "../components/RotationField";
import ToggleRow from "../components/ToggleRow";
import UnitHeader from "../components/UnitHeader";
import {
  degreesToRadians,
  getCommonDegreesValue,
  getCommonValue,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";
import type { UpdateGlobalState } from "../types";

type BookShelfSectionProps = {
  selectedBookShelves: BookShelfType[];
  displayedBookShelves: BookShelfType[];
  updateGlobalState: UpdateGlobalState;
};

const BookShelfSection: React.FC<BookShelfSectionProps> = observer(({
  selectedBookShelves,
  displayedBookShelves,
  updateGlobalState,
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  const getTitle = () => {
    if (selectedBookShelves.length > 1) {
      return t("propertyPanel.bookShelves", {
        count: selectedBookShelves.length,
      });
    }
    return t("propertyPanel.bookShelf");
  };

  if (displayedBookShelves.length === 0) return null;

  const updateConfigs = (
    mutate: (config: BookShelfConfig) => BookShelfConfig,
  ) => {
    selectedBookShelves.forEach((symbol) => {
      updateGlobalState(
        [symbol],
        "bookShelfConfig",
        mutate(symbol.bookShelf),
      );
    });
  };

  const commonConfigValue = <T,>(
    getValue: (config: BookShelfConfig) => T,
  ): T | undefined =>
    getCommonValue(selectedBookShelves, (symbol) =>
      getValue(symbol.bookShelf),
    );

  const dimensionValue = (
    getValue: (config: BookShelfConfig) => number,
  ): number | "" => {
    const common = commonConfigValue(getValue);
    return common === undefined ? "" : Math.round(toDisplayLength(common));
  };

  const commonRotation = getCommonDegreesValue(selectedBookShelves);
  const commonFloorDistance = getCommonValue(
    selectedBookShelves,
    (symbol) =>
      Math.round(toDisplayLength(symbol.threeD?.baseElevation ?? 0)),
  );
  const commonHasFrame = commonConfigValue((config) => config.hasFrame);
  const commonHasBackPanel = commonConfigValue(
    (config) => config.hasBackPanel,
  );
  const commonShelfCount = commonConfigValue((config) => config.shelfCount);

  return (
    <div data-testid="book-shelf-section">
      <div className="border-b border-gray-300" />
      <div className="h-[32px] px-2.5 flex items-center justify-between text-sm font-normal">
        <UnitHeader title={getTitle()} />
      </div>
      <div className="border-b border-gray-300" />

      <div className="p-3 pb-1 pl-4 text-sm">
        {t("propertyPanel.dimensions")}
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 py-2">
        <DimensionField
          label={t("propertyPanel.width")}
          min={toDisplayLength(BOOK_SHELF_MIN_WIDTH_M)}
          max={toDisplayLength(BOOK_SHELF_MAX_WIDTH_M)}
          value={dimensionValue((config) => config.width)}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                width: toWorldFromDisplay(parsed),
              }),
            );
          }}
        />
        <DimensionField
          label={t("propertyPanel.depth")}
          min={toDisplayLength(BOOK_SHELF_MIN_DEPTH_M)}
          max={toDisplayLength(BOOK_SHELF_MAX_DEPTH_M)}
          value={dimensionValue((config) => config.depth)}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                depth: toWorldFromDisplay(parsed),
              }),
            );
          }}
        />
        <DimensionField
          label={t("propertyPanel.height")}
          min={toDisplayLength(BOOK_SHELF_MIN_HEIGHT_M)}
          max={toDisplayLength(BOOK_SHELF_MAX_HEIGHT_M)}
          value={dimensionValue((config) => config.height)}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            const worldHeight = toWorldFromDisplay(parsed);
            updateConfigs((config) => {
              const shelfSpacing = deriveBookShelfSpacingFromHeight(
                worldHeight,
                config.shelfCount,
              );
              return normalizeBookShelfConfig({
                ...config,
                shelfSpacing,
              });
            });
          }}
        />
        <RotationField
          value={commonRotation ?? ""}
          placeholder={mixed}
          onChange={(degrees) =>
            updateGlobalState(
              selectedBookShelves,
              "rotation",
              degreesToRadians(degrees),
            )
          }
        />
        <DimensionField
          label={t("propertyPanel.distanceFromFloor")}
          min={0}
          value={commonFloorDistance ?? ""}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            selectedBookShelves.forEach((symbol) => {
              updateGlobalState(
                [symbol],
                "symbol3DBaseElevation",
                parsed,
              );
            });
          }}
        />
      </div>

      <div className="p-3 pb-1 pl-4 text-sm">
        {t("propertyPanel.shelves")}
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 py-2">
        <NumberField
          label={t("propertyPanel.shelfCount")}
          min={BOOK_SHELF_MIN_SHELF_COUNT}
          max={BOOK_SHELF_MAX_SHELF_COUNT}
          step={1}
          value={commonShelfCount ?? ""}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseInt(value, 10);
            if (!Number.isFinite(parsed)) return;
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                shelfCount: parsed,
              }),
            );
          }}
        />
        <DimensionField
          label={t("propertyPanel.shelfSpacing")}
          min={toDisplayLength(BOOK_SHELF_MIN_SHELF_SPACING_M)}
          max={toDisplayLength(BOOK_SHELF_MAX_SHELF_SPACING_M)}
          value={dimensionValue((config) => config.shelfSpacing)}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                shelfSpacing: toWorldFromDisplay(parsed),
              }),
            );
          }}
        />
      </div>
      <div className="px-3 pb-2">
        <ToggleRow
          label={t("propertyPanel.frame")}
          checked={commonHasFrame ?? false}
          indeterminate={commonHasFrame === undefined}
          onChange={(hasFrame) =>
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                hasFrame,
              }),
            )
          }
        />
        <ToggleRow
          label={t("propertyPanel.backPanel")}
          checked={commonHasBackPanel ?? false}
          indeterminate={commonHasBackPanel === undefined}
          onChange={(hasBackPanel) =>
            updateConfigs((config) =>
              normalizeBookShelfConfig({
                ...config,
                hasBackPanel,
              }),
            )
          }
        />
      </div>
    </div>
  );
});

export default BookShelfSection;
